<template>
    <!-- Table -->
    <table-generic
    v-if="tableData.length && !showEmptyState"
    :headerData="tableHeaderData"
    :tableData="tableData"
    :tableStyles="tableStyles"
    :hasMore="getFinancialPlanHasMore"
    :isLoadingMore="tableLoadingMore"
    :loadingMoreText="t('credits.myCredits.movements.loadingMoreText')"
    @loadMore="emit('loadMoreFinancialPlanList')"
    @rowClicked="rowClicked($event)"/>
    <!-- Empty State -->
    <empty-state-generic
    v-if="showEmptyState"
    illustration="emptyState/no-movements"
    :title="t('credits.myCredits.installments.emptyStates.title')"
    :description="t('credits.myCredits.installments.emptyStates.description')"/>
    <!-- Modal -->
    <installments-modal
    v-if="showModal"
    :detailHeaderParams="detailHeaderParams"
    :payloadDetailModal="payloadDetailModal"
    @closeMe="showModal = false"/>
</template>

<script setup>
import TableGeneric from '@/components/generic/table/Generic'
import InstallmentsModal from '@/components/features/credits/myCredits/installments/DetailModal'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import { moduleMapper } from '@/store/helpers/modules.map'
import { mapGetters } from '@/store/helpers/mappers'

/* Aux */
import { getDate } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { currencyParser } from '@/assets/scripts/amounts'
import { amountFormat } from '@/assets/scripts/amounts'
import { iconMapper, iconColorMapper, iconMapperModal, colorStatusMapperModal } from '@/assets/scripts/mappers/credit'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapGetters */
const { getMobileAppState } = mapGetters()
const { getFinancialPlanList, getFinancialPlanHasMore } = mapGetters(moduleMapper.CREDITS_MY_CREDITS)

/* Emmitters */
const emit = defineEmits(['loadMoreFinancialPlanList'])

/* Props received:
@currency { String }
@showEmptyState { Boolean }
*/
const props = defineProps({
    currency: String,
    showEmptyState: Boolean,
    tableLoadingMore: Boolean
})

/* Data */
const showModal = ref(false)
const installmentsRow = ref()

/*************************************************
*                                                *
*                     Table                      *
*                                                *
*************************************************/
const tableHeaderData = [
	{
        label: t('credits.myCredits.installments.tableHeaders.installmentNumber'),
        showInMobile: { groupWith: ['amount', 'paymentDate'], groupInline: true },
        rowCellType: 'Title',
        dataAttr: 'installmentNumber'
    },
    { label: t('credits.myCredits.installments.tableHeaders.amount'), rowCellType: 'description', dataAttr: 'amount' },
    { label: t('credits.myCredits.installments.tableHeaders.paymentDate'), dataAttr: 'paymentDate' },
    { label: t('credits.myCredits.installments.tableHeaders.capitalDebt'), dataAttr: 'capitalDebt' },
    { label: t('credits.myCredits.installments.tableHeaders.situation'), rowCellType: 'status', dataAttr: 'situation', showInMobile: true }
]

const tableStyles = [null, { 'text-align': 'right' }, { 'text-align': 'right' }, { 'text-align': 'right' }, null]

const tableData = computed(() => {
    if (!getFinancialPlanList.value) return []

    return getFinancialPlanList.value.map(obj => {
        return [
            { value: `${obj.numbStandardizationOperation}${obj.recordType}${obj.numbAmortization}`, dataAttr: 'id' },
            { type: 'title', value: { content: obj.numbAmortization, mask: ACCOUNT_NUMBER }, dataAttr: 'installmentNumber' },
            { type: 'amount', value: { content: obj.valueAmortizationRent, currency: currencyParser(props.currency), noBalanceIcon: true }, dataAttr: 'amount' },
            { type: 'description', value: { content: getDate(obj.maturityExpiration), classes: !getMobileAppState.value ? 'capitalize' : ['capitalize', 'small', 'secondary-color'] }, dataAttr: 'paymentDate' },
            { type: 'amount', value: { content: obj.capitalDebt, currency: currencyParser(props.currency), noBalanceIcon: true }, dataAttr: 'capitalDebt' },
            { type: 'status', value: { text: obj.description, icon: iconMapper(obj.situation), classes: iconColorMapper(obj.situation) }, dataAttr: 'situation' }
        ]
    })
})

const rowClicked = $event => {
    installmentsRow.value = getFinancialPlanList.value.find(obj => `${obj.numbStandardizationOperation}${obj.recordType}${obj.numbAmortization}` === $event.rowId)
    setDetailInfo()
    showModal.value = true
}

const detailHeaderParams = ref({})
const payloadDetailModal = ref({})

const setDetailInfo = () => {
    detailHeaderParams.value = {
        color: colorStatusMapperModal(installmentsRow.value.situation),
        bigIcon: iconMapperModal(installmentsRow.value.situation)
    }

    payloadDetailModal.value = {
        numbAmortization: installmentsRow.value.numbAmortization,
        valueAmortizationRent: installmentsRow.value.valueAmortizationRent,
        amount: amountFormat(Math.abs(installmentsRow.value.valueAmortizationRent), props.currency),
        date: installmentsRow.value.situationDate ? getDate(installmentsRow.value.situationDate) : '-',
        situation: {
            code: installmentsRow.value.situation,
            icon: iconMapper(installmentsRow.value.situation),
            content: installmentsRow.value.description,
            color: colorStatusMapperModal(installmentsRow.value.situation)
        },
        interestValue: installmentsRow.value.interestValue || installmentsRow.value.interestValue === 0
                        ? amountFormat(Math.abs(installmentsRow.value.interestValue), props.currency) : '-',
        expenses: installmentsRow.value.totalExpenses || installmentsRow.value.totalExpenses === 0 ? amountFormat(installmentsRow.value.totalExpenses, props.currency) : '-',
        capital: amountFormat(Math.abs(installmentsRow.value.capitalValue), props.currency),
        tan: installmentsRow.value.nominalTax,
        capitalDebt: amountFormat(installmentsRow.value.capitalDebt, props.currency)
    }
}
</script>

<style lang="scss" scoped>
</style>
